import { useEffect, useState } from 'react'
import { Button, Spin } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { getStockFinancedetail } from '@/api/stock'
import type { StockFinancialData } from '@/types/stockPool'
import { mockStocks } from '../../mockData'
import './index.scss'

// ── 报告类型标签 ────────────────────────────────────────────────────────────
export const reportTypeLabel: Record<string, string> = {
    annual: '年报', interim_h1: '中报(H1)', interim_h2: '中报(H2)',
    quarterly_q1: 'Q1季报', quarterly_q2: 'Q2季报',
    quarterly_q3: 'Q3季报', quarterly_q4: 'Q4季报',
    quarterly: '季报', research: '研报',
}

// ── 格式化工具 ──────────────────────────────────────────────────────────────
function fmtMoney(v: number | null | undefined, cur: string) {
    if (v == null) return '—'
    const sym = cur === 'HKD' ? 'HK$' : cur === 'USD' ? '$' : '¥'
    if (Math.abs(v) >= 1e12) return sym + (v / 1e12).toFixed(3) + ' 万亿'
    if (Math.abs(v) >= 1e8)  return sym + (v / 1e8).toFixed(2)  + ' 亿'
    if (Math.abs(v) >= 1e4)  return sym + (v / 1e4).toFixed(2)  + ' 万'
    return sym + v.toFixed(2)
}
function fmtPct(v: number | null | undefined) {
    if (v == null) return '—'
    return (v >= 0 ? '+' : '') + v.toFixed(2) + '%'
}
function fmtX(v: number | null | undefined, dp = 2) {
    if (v == null || v === 0) return '—'
    return v.toFixed(dp) + 'x'
}
function fmtNum(v: number | null | undefined, dp = 2) {
    if (v == null) return '—'
    return v.toFixed(dp)
}
function fmtStr(v: string | null | undefined) { return v?.trim() || '—' }
function fmtShares(v: number | null | undefined) {
    if (!v) return '—'
    return (v / 1e8).toFixed(4) + ' 亿股'
}
function fmtJson(v: Record<string, string> | null | undefined) {
    if (!v || !Object.keys(v).length) return '—'
    return Object.entries(v).map(([k, val]) => `${k}：${val}`).join('\n')
}

// ── 字段定义（顺序即为表格行顺序）──────────────────────────────────────────
export type FieldDef = { label: string; section?: string; render: (d: StockFinancialData) => string }

export function buildFields(cur: string): FieldDef[] {
    const m = (v: number | null | undefined) => fmtMoney(v, cur)
    return [
        // 基本信息
        { section: '基本信息', label: '股票代码',      render: d => d.stock_code },
        { label: '报告期',          render: d => fmtStr((d as any).fiscal_year) },
        { label: '报告类型',        render: d => reportTypeLabel[(d as any).report_type ?? ''] ?? fmtStr((d as any).report_type) },
        { label: '报告日期',        render: d => fmtStr(d.report_date) },
        { label: '收盘日期',        render: d => fmtStr(d.closing_date) },
        { label: '报告期收盘价',    render: d => d.closing_price != null ? `${cur === 'HKD' ? 'HK$' : cur === 'USD' ? '$' : '¥'}${d.closing_price.toFixed(2)}` : '—' },
        { label: '币种',            render: d => fmtStr(d.currency) },

        // 利润表
        { section: '利润表', label: '营业收入',          render: d => m(d.revenue) },
        { label: '收入同比增长',                          render: d => fmtPct(d.revenue_yoy_pct) },
        { label: '营收同比（口径二）',                    render: d => fmtPct(d.operating_revenue_yoy_pct) },
        { label: '收入来源',                              render: d => fmtStr(d.revenue_sources) },
        { label: '收入增长驱动',                          render: d => fmtStr(d.revenue_growth_drivers) },
        { label: '毛利润',                                render: d => m(d.gross_profit) },
        { label: '毛利率',                                render: d => fmtPct(d.gross_margin_pct) },
        { label: '销售及分销费用',                        render: d => m(d.selling_distribution_expense) },
        { label: '行政开支',                              render: d => m(d.admin_expense) },
        { label: '财务费用',                              render: d => m(d.finance_cost) },
        { label: '期间费用率',                            render: d => fmtPct(d.period_expense_ratio_pct) },
        { label: '税前利润',                              render: d => m(d.profit_before_tax) },
        { label: '所得税',                                render: d => m(d.income_tax) },
        { label: '归母净利润',                            render: d => m(d.net_profit_parent) },
        { label: '净利率',                                render: d => fmtPct(d.net_margin_pct) },
        { label: '调整后净利润（Non-IFRS）',              render: d => m(d.adjusted_net_profit_non_ifrs) },
        { label: '调整后净利润同比',                      render: d => fmtPct(d.adjusted_net_profit_yoy_pct) },
        { label: '扣非净利润',                            render: d => m(d.net_profit_ex_non_recurring) },
        { label: '扣非净利润同比',                        render: d => fmtPct(d.net_profit_ex_non_recurring_yoy_pct) },
        { label: 'EBITDA',                               render: d => m(d.ebitda) },

        // 每股指标
        { section: '每股指标', label: '基本每股收益（EPS）', render: d => fmtNum(d.eps_basic) },
        { label: '稀释每股收益',     render: d => fmtNum(d.eps_diluted) },
        { label: '普通股每股收益',   render: d => fmtNum(d.eps_common) },
        { label: '期末总股本',        render: d => fmtShares(d.total_shares_outstanding_end) },

        // 盈利能力
        { section: '盈利能力', label: 'ROE（净资产收益率）', render: d => fmtPct(d.roe_pct) },
        { label: 'ROA（总资产收益率）', render: d => fmtPct(d.roa_pct) },

        // 资产负债表
        { section: '资产负债表', label: '总资产',        render: d => m(d.total_assets) },
        { label: '流动资产',          render: d => m(d.current_assets) },
        { label: '货币资金',          render: d => m(d.cash_and_equivalents) },
        { label: '总负债',            render: d => m(d.total_liabilities) },
        { label: '流动负债',          render: d => m(d.current_liabilities) },
        { label: '有息负债',          render: d => m(d.interest_bearing_debt) },
        { label: '资产负债率',        render: d => fmtPct(d.debt_to_asset_ratio_pct) },
        { label: '流动比率',          render: d => fmtX(d.current_ratio) },
        { label: '净资产（股东权益）', render: d => m(d.net_assets_equity) },
        { label: '归母权益',          render: d => m(d.parent_equity) },
        { label: '总权益（含少数股东）', render: d => m(d.total_equity) },

        // 现金流量表
        { section: '现金流量表', label: '经营活动现金流（CFO）', render: d => m(d.operating_cash_flow_cfo) },
        { label: '投资活动现金流（CFI）', render: d => m(d.investing_cash_flow_cfi) },
        { label: '筹资活动现金流（CFF）', render: d => m(d.financing_cash_flow_cff) },
        { label: '自由现金流（FCF）',     render: d => m(d.free_cash_flow_fcf) },

        // 估值指标
        { section: '估值指标（报告期）', label: '市值',  render: d => m(d.market_cap) },
        { label: '市盈率（PE）',          render: d => fmtX(d.pe_ratio) },
        { label: 'PE(TTM)',               render: d => fmtX(d.pe_ttm) },
        { label: '市净率（PB）',          render: d => fmtX(d.pb_ratio) },
        { label: '市销率（PS）',          render: d => fmtX(d.ps_ratio) },
        { label: 'EV/EBITDA',            render: d => fmtX(d.ev_ebitda, 1) },
        { label: '股息率',               render: d => d.dividend_yield_pct && d.dividend_yield_pct > 0 ? fmtPct(d.dividend_yield_pct) : '—' },

        // 分红与资本行动
        { section: '分红与资本行动', label: '分红总额',  render: d => m(d.total_dividend) },
        { label: '分红派息率',            render: d => fmtPct(d.dividend_payout_ratio_pct) },
        { label: '每股派息',              render: d => d.dividend_per_share ? fmtNum(d.dividend_per_share, 4) : '—' },
        { label: '每股红股',              render: d => d.stock_dividend_per_share ? fmtNum(d.stock_dividend_per_share, 4) : '—' },
        { label: '除净日',               render: d => fmtStr(d.ex_dividend_date) },
        { label: '派息日',               render: d => fmtStr(d.dividend_payment_date) },
        { label: '股票回购金额',          render: d => m(d.share_buyback_amount) },
        { label: '供股详情',              render: d => fmtStr(d.rights_issue_details) },
        { label: '合股比例',              render: d => fmtStr(d.share_consolidation_ratio as any) },
        { label: '拆股比例',              render: d => fmtStr(d.stock_split_ratio as any) },
        { label: '配股详情',              render: d => fmtStr(d.placement_details) },
        { label: '增发详情',              render: d => fmtStr(d.additional_issuance_details) },
        { label: '停牌日期',              render: d => fmtStr(d.suspension_date) },
        { label: '复牌日期',              render: d => fmtStr(d.resumption_date) },

        // 业务KPI
        { section: '业务运营指标', label: 'KPI 指标',      render: d => fmtJson(d.kpi_metrics) },
        { label: '管理层前瞻指引',                          render: d => fmtJson(d.kpi_guidance) },
        { label: '非GAAP / 非IFRS 指标',                   render: d => fmtJson(d.kpi_non_gaap_metrics) },

        // 文本分析
        { section: '文本分析', label: '重大事项',  render: d => fmtStr((d as any).annual_report_major_events) },
    ]
}

// ── AI Step 块 ──────────────────────────────────────────────────────────────
export function StepBlock({ title, content }: { title: string; content: string }) {
    let display = content.trim()
    if (display.startsWith('```')) {
        display = display.replace(/^```(?:json)?\s*\n?/, '').replace(/\n?```\s*$/, '')
        try { display = JSON.stringify(JSON.parse(display), null, 2) } catch { /* keep as-is */ }
    }
    return (
        <div className="rpt__step-block">
            <div className="rpt__step-title">{title}</div>
            <pre className="rpt__step-pre">{display}</pre>
        </div>
    )
}

// ── 主组件 ──────────────────────────────────────────────────────────────────
export default function ReportDetailPage() {
    const navigate = useNavigate()
    const { code, reportId } = useParams<{ code: string; reportId: string }>()
    const [ind, setInd] = useState<StockFinancialData | null>(null)
    const [stockName, setStockName] = useState('')
    const [loading, setLoading] = useState(true)

    const decodedCode = decodeURIComponent(code ?? '')

    useEffect(() => {
        if (!code || !reportId) return
        setLoading(true)
        getStockFinancedetail(decodedCode)
            .then(res => {
                setStockName(res.name ?? decodedCode)
                const found = res.items.find(i => i.id === Number(reportId))
                if (found) {
                    setInd(found)
                } else {
                    const mock = mockStocks.find(s => s.code === decodedCode)
                    const mockInd = mock?.indicators.find(i => i.id === Number(reportId))
                    if (mockInd) setInd(mockInd as any)
                    if (mock) setStockName(mock.name)
                }
            })
            .catch(() => {
                const mock = mockStocks.find(s => s.code === decodedCode)
                const mockInd = mock?.indicators.find(i => i.id === Number(reportId))
                if (mockInd) setInd(mockInd as any)
                if (mock) setStockName(mock.name)
            })
            .finally(() => setLoading(false))
    }, [code, reportId])

    if (loading) return (
        <div className="rpt" style={{ justifyContent: 'center', alignItems: 'center' }}>
            <Spin size="large" />
        </div>
    )

    if (!ind) return (
        <div className="rpt">
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-3)' }}>
                财报记录不存在
                <br />
                <Button style={{ marginTop: 16 }} onClick={() => navigate(`/stock-kb/${code}`)}>返回</Button>
            </div>
        </div>
    )

    const cur = ind.currency ?? 'HKD'
    const anyInd = ind as any
    const rtLabel = reportTypeLabel[anyInd.report_type ?? ''] ?? anyInd.report_type ?? '—'
    const fields = buildFields(cur)

    return (
        <div className="rpt">
            {/* 顶部标题栏 */}
            <div className="page-header flex flex-between rpt__header">
                <div className="rpt__title-group">
                    <Button size="small" type="text" className="rpt__back" icon={<ArrowLeftOutlined />}
                        onClick={() => navigate(`/stock-kb/${encodeURIComponent(decodedCode)}?tab=2`)}>
                        {stockName || decodedCode}
                    </Button>
                    /
                    <span className="rpt__period">{anyInd.fiscal_year ?? '—'}</span>
                    <span className="rpt__type-tag">{rtLabel}</span>
                    <span className="rpt__date-hint">{ind.report_date}</span>
                </div>
                <div className="rpt__header-right">
                    <span className="rpt__price-label">报告期收盘</span>
                    <span className="rpt__price">
                        {cur === 'HKD' ? 'HK$' : cur === 'USD' ? '$' : '¥'}{ind.closing_price?.toFixed(2) ?? '—'}
                    </span>
                </div>
            </div>

            {/* 字段列表 */}
            <div className="rpt__content">
                <table className="rpt__table">
                    <tbody>
                        {fields.map((f, i) => (
                            <>
                                {f.section && (
                                    <tr key={`sec-${i}`} className="rpt__section-row">
                                        <td colSpan={2}>{f.section}</td>
                                    </tr>
                                )}
                                <tr key={f.label} className="rpt__row">
                                    <td className="rpt__row-label">{f.label}</td>
                                    <td className="rpt__row-value">{f.render(ind)}</td>
                                </tr>
                            </>
                        ))}
                    </tbody>
                </table>
                {anyInd.indicators_step_1_md && (
                    <StepBlock title="AI 提取数据（Step 1）" content={anyInd.indicators_step_1_md} />
                )}
                {anyInd.indicators_step_2_md && (
                    <StepBlock title="AI 提取数据（Step 2）" content={anyInd.indicators_step_2_md} />
                )}
            </div>
        </div>
    )
}
